import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
} from '@angular/material/legacy-dialog';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { IAppIntegrationShippingPreset } from '../../../app-integration-account/intefaces/app-integration.interface';
import {
  weightUnits,
  dimensionUnits,
} from '../../../invoice/config/shipstation.config';
import { EWeightUnit } from '../../../invoice/interfaces/shipstation-shipment.interface';
import {
  IOrderSalesPicklistPreviewOrder,
  IOrderSalesShipstationAddress,
} from '../../../order/interfaces/order-picklist-preview';
import { ESalesOrderStatusValue } from '../../../sales-order/interfaces';
import {
  DialogConfig,
  IWeight,
  SnackBarConfig,
  trackByControlValue,
  trackByValue,
  trackById,
  FeatureFlagUtilService,
} from '../../../shared';
import { OptionInterface } from '../../../shared/interfaces/common.interface';
import { ShippingLabelFacade } from '../../facade/shipping-label.facade';
import {
  IShipStationCarriers,
  IShipstationPackagesServices,
  IShipstationRates,
} from '../../interfaces';
import { ShippingLabelComponent } from '../shipping-label/shipping-label.component';

@Component({
  selector: 'lum-bulk-shipping-label',
  templateUrl: './bulk-shipping-label.component.html',
  styleUrls: ['./bulk-shipping-label.component.scss'],
})
export class BulkShippingLabelComponent implements OnInit {
  form :UntypedFormGroup;
  loadingCarriers :boolean = false;
  submittingForm :boolean = false;
  carriers :IShipStationCarriers[] = [];
  servicesByCarrierCode :{ [key :string] :IShipstationPackagesServices[] } = {};
  packagesByCarrierCode :{ [key :string] :IShipstationPackagesServices[] } = {};
  loadingServicesByCarrierCode :{ [key :string] :boolean } = {};
  loadingPackagesByCarrierCode :{ [key :string] :boolean } = {};
  selectAll :UntypedFormControl = new UntypedFormControl(false);

  weightUnits :OptionInterface[] = weightUnits;
  dimensionUnits :OptionInterface[] = dimensionUnits;
  weightUnitLabels :string[] = [];
  dimensionsUnitLabels :string[] = [];
  fieldNames :{ [key :string] :string } = {
    weight: 'weight.units',
    dimensions: 'dimensions.units',
  };

  currentPreset :IAppIntegrationShippingPreset | null = null;
  canApplyPreset :boolean = false;
  private onDestroy$ = new Subject();

  trackByControlValue = trackByControlValue;
  trackByValue = trackByValue;
  trackById = trackById;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data :{
      appIntegrationAccountId :number;
      orders :IOrderSalesPicklistPreviewOrder[];
      warehouseAddress ?:IOrderSalesShipstationAddress | null;
      ftApplyPresets :boolean;
    },
    private dialogRef :MatDialogRef<BulkShippingLabelComponent>,
    private snackBar :MatSnackBar,
    private fb :UntypedFormBuilder,
    private shippingLabelFacade :ShippingLabelFacade,
    private dialog :MatDialog,
    private featureFlagUtilService :FeatureFlagUtilService,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.loadCarriersData();
    this.checkFormValueChanges();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onApplyPreset(preset :IAppIntegrationShippingPreset | null) :void {
    this.currentPreset = preset;
    this.initForm(preset);
    this.canApplyPreset = true;
  }

  onSelectAll(selected :boolean = false) :void {
    this.lineItemsFormArray.controls.forEach((lineItem) => {
      lineItem.patchValue({
        selected: selected,
      });
    });
  }

  onClose() :void {
    this.dialogRef.close();
  }

  onSetUnit(field :string, unit :string, index :number) :void {
    if (field === this.fieldNames['weight']) {
      const weightUnit = this.weightUnits.find((obj) => obj.value === unit);
      this.weightUnitLabels[index] = weightUnit?.label || '';
    } else if (field === this.fieldNames['dimensions']) {
      const dimensionUnit = this.dimensionUnits.find((obj) => obj.value === unit);
      this.dimensionsUnitLabels[index] = dimensionUnit?.label || '';
    }

    this.lineItemsFormArray.at(index).get(field)?.patchValue(unit);
  }

  onSelectCarrier(event :MatSelectChange, index :number) :void {
    const lineItem = this.lineItemsFormArray.at(index);
    if (!this.servicesByCarrierCode.hasOwnProperty(event.value)) {
      this.loadPackageServices(event.value, lineItem.get('salesOrderId')?.value);
    }
    lineItem.patchValue({
      packageCode: '',
      serviceCode: '',
    });
  }

  onBulkUpdate() :void {
    this.dialog
      .open(ShippingLabelComponent, {
        data: {
          appIntegrationAccountId: this.data.appIntegrationAccountId,

          isBulkUpdate: true,
        },
        width: DialogConfig.sizes.medium,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((response) => {
        if (response) {
          this.lineItemsFormArray.controls.forEach((control, idx) => {
            if (control.get('selected')?.value) {
              const weightUnit = this.weightUnits.find(
                (obj) => obj.value === response.weight.units,
              );
              this.weightUnitLabels[idx] = weightUnit?.label || '';

              const dimensionUnit = this.dimensionUnits.find(
                (obj) => obj.value === response.dimensions.units,
              );
              this.dimensionsUnitLabels[idx] = dimensionUnit?.label || '';

              control.patchValue({
                carrierCode: response.carrierCode,
                serviceCode: response.serviceCode,
                packageCode: response.packageCode,
                weight: response.weight,
                dimensions: response.dimensions,
              });
              if (response.carrierCode) {
                this.loadPackageServices(
                  response.carrierCode,
                  control.get('salesOrderId')?.value,
                );
              }
              this.onGetRates(control.value);
            }
          });
        }
      });
  }

  onRefreshRate(lineItem :any) :void {
    if (lineItem.shipRateLoading) {
      return;
    }
    this.onGetRates(lineItem, true);
  }

  onSubmit() :void {
    if (this.form.valid) {
      this.submittingForm = true;

      const payload = {
        ...this.form.value,
        lineItems: this.form.value.lineItems.map((lineItem :any) => {
          const { salesOrderShipments, hasMultipleShipments, ...rest } = lineItem;

          return {
            ...rest,
            weight: this.getLineItemWeight(lineItem.weight),
          };
        }),
      };
      this.shippingLabelFacade
        .createBulkShippingLabel(payload)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response :any) => {
            if (response) {
              this.snackBar.open(response.success, 'Success', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
            }
            this.submittingForm = false;
            this.onClose();
          },
          error: (response :any) => {
            this.snackBar.open(response.error, 'Error', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.submittingForm = false;
          },
        });
    }
  }

  private initForm(preset ?:IAppIntegrationShippingPreset | null) :void {
    this.form = this.fb.group({
      shipDate: [moment().format('YYYY-MM-DD'), Validators.required],
      confirmation: ['none'],
      lineItems: this.fb.array(
        this.data.orders.map((item, idx) => {
          const canApplyPreset =
            (!item?.carrierCode && preset?.carrierCode) || this.canApplyPreset;
          const shipTo = JSON.parse(item.shipTo);
          let updatedItem = item;
          let weight = item.weight ? JSON.parse(item.weight?.toString()) : {};
          let dimensions = item.dimensions
            ? JSON.parse(item.dimensions?.toString())
            : {};

          if (canApplyPreset) {
            updatedItem = {
              ...updatedItem,
              carrierCode: preset?.carrierCode || '',
              serviceCode: preset?.serviceCode || '',
              packageCode: preset?.packageCode || '',
              dimensions: JSON.stringify(preset?.dimensions),
              confirmation: preset?.confirmation || '',
              weight: JSON.stringify(preset?.weight),
            };

            dimensions = updatedItem.dimensions
              ? JSON.parse(updatedItem.dimensions)
              : {};
            weight = updatedItem.weight ? JSON.parse(updatedItem.weight) : {};
          }

          if (updatedItem.carrierCode) {
            this.loadPackageServices(updatedItem.carrierCode, item.id);
          }

          const weightValue = weight?.value ?? 0;
          const weightUnitString = weight?.units ?? 'pounds';
          const weightUnit = this.weightUnits.find(
            (obj) => obj.value === weightUnitString,
          );
          this.weightUnitLabels[idx] = weightUnit?.label || '';

          const dimensionUnitString = dimensions?.units ?? 'inches';
          const dimensionUnit = this.dimensionUnits.find(
            (obj) => obj.value === dimensionUnitString,
          );
          this.dimensionsUnitLabels[idx] = dimensionUnit?.label || '';

          return this.fb.group({
            salesOrderId: [updatedItem.id],
            salesOrderNumber: [updatedItem.orderNumber],
            carrierCode: [updatedItem.carrierCode || '', Validators.required],
            serviceCode: [updatedItem.serviceCode || '', Validators.required],
            packageCode: [updatedItem.packageCode || '', Validators.required],
            weight: this.fb.group({
              pounds: this.fb.group({
                units: [EWeightUnit.POUNDS],
                value: [
                  weightUnitString === EWeightUnit.POUNDS ? weightValue : 0,
                  [Validators.required],
                ],
              }),
              ounces: this.fb.group({
                units: [EWeightUnit.OUNCES],
                value: [
                  weightUnitString === EWeightUnit.OUNCES ? weightValue : 0,
                  [Validators.required],
                ],
              }),
            }),
            dimensions: this.fb.group({
              units: [dimensionUnitString],
              length: [dimensions?.length ?? 0],
              width: [dimensions?.width ?? 0],
              height: [dimensions?.height ?? 0],
            }),
            shipTo: [{ ...shipTo, company: shipTo?.company || 'N/A' }],
            shipToString: [
              shipTo ? this.shippingLabelFacade.getAddressText(shipTo) : '',
            ],
            shipFrom: [this.data.warehouseAddress ?? null],
            selected: [false],
            orderItemsCount: [item.orderItemsCount ?? 0],
            shipRate: [0],
            shipRateLoading: [false],
            salesOrderShipments: [item.salesOrderShipments],
            hasMultipleShipments: [item.hasMultipleShipments],
            salesOrderShipmentId: [
              null,
              item.hasMultipleShipments ? Validators.required : null,
            ],
          });
        }),
      ),
    });

    // Set initial rates for each line item on load
    this.setInitialRates();
  }

  private checkFormValueChanges() :void {
    const lineItemsFormArray = this.form.get('lineItems') as UntypedFormArray;
    lineItemsFormArray.controls.forEach((abstractControl :AbstractControl) => {
      if (abstractControl instanceof UntypedFormGroup) {
        abstractControl.valueChanges
          .pipe(debounceTime(900))
          .subscribe((changes) => {
            this.onGetRates(changes);
          });
      }
    });
  }

  private setInitialRates() :void {
    const lineItemsFormArray = this.form.get('lineItems') as UntypedFormArray;
    lineItemsFormArray.controls.forEach((control :AbstractControl) => {
      if (control instanceof UntypedFormGroup) {
        const { carrierCode } = control.value;
        if (carrierCode) {
          this.onGetRates(control.value);
        }
      }
    });
  }

  private loadCarriersData() :void {
    let params :{ appIntegrationAccountId ?:string } = {};

    if (this.data.appIntegrationAccountId) {
      params = {
        appIntegrationAccountId: this.data.appIntegrationAccountId.toString(),
      };
    }

    this.loadingCarriers = true;
    const appIntegrationAccountId = this.data?.appIntegrationAccountId || 0;

    this.shippingLabelFacade
      .loadCarriersData(appIntegrationAccountId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          if (response) {
            this.carriers = response;
          }
          this.loadingCarriers = false;
        },
        error: (response :any) => {
          this.snackBar.open(response.error, 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.loadingCarriers = false;
        },
      });
  }

  private loadPackageServices(carrierCode :string, salesOrderId :number = 0) :void {
    this.loadServicesData(carrierCode, salesOrderId);
    this.loadPackagesData(carrierCode, salesOrderId);
  }

  private loadServicesData(carrierCode :string, salesOrderId :number = 0) :void {
    let params :{
      appIntegrationAccountId ?:string;
      carrierCode ?:string;
      salesOrderId ?:string;
    } = {
      carrierCode,
      ...(salesOrderId > 0 && {
        salesOrderId: salesOrderId.toString(),
      }),
    };

    if (this.data.appIntegrationAccountId) {
      params = {
        carrierCode,
        appIntegrationAccountId: this.data.appIntegrationAccountId.toString(),
      };
    }

    this.loadingServicesByCarrierCode[carrierCode] = true;
    this.shippingLabelFacade
      .loadServices(carrierCode, salesOrderId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          if (response) {
            this.servicesByCarrierCode[carrierCode] = response;
          }
          this.loadingServicesByCarrierCode[carrierCode] = false;
        },
        error: (response :any) => {
          this.snackBar.open(response.error, 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.loadingServicesByCarrierCode[carrierCode] = false;
        },
      });
  }

  private loadPackagesData(carrierCode :string, salesOrderId :number = 0) :void {
    let params :{
      appIntegrationAccountId ?:string;
      carrierCode ?:string;
      salesOrderId ?:string;
    } = {
      carrierCode: carrierCode,
      ...(salesOrderId > 0 && {
        salesOrderId: salesOrderId.toString(),
      }),
    };

    if (this.data.appIntegrationAccountId) {
      params = {
        carrierCode,
        appIntegrationAccountId: this.data.appIntegrationAccountId.toString(),
      };
    }

    this.loadingPackagesByCarrierCode[carrierCode] = true;
    this.shippingLabelFacade
      .loadPackages(carrierCode, salesOrderId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          if (response) {
            this.packagesByCarrierCode[carrierCode] = response;
          }
          this.loadingPackagesByCarrierCode[carrierCode] = false;
        },
        error: (response :any) => {
          this.snackBar.open(response.error, 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.loadingPackagesByCarrierCode[carrierCode] = false;
        },
      });
  }

  private onGetRates(lineItem :any, showRates :boolean = false) :void {
    if (!lineItem.carrierCode) {
      return;
    }
    this.updateLineItemRate(lineItem.salesOrderId, {
      shipRate: 0,
      shipRateLoading: true,
    });

    this.shippingLabelFacade
      .getSortedRates(lineItem, showRates, this.data.warehouseAddress)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (rate ?:IShipstationRates) => {
          const updateValue :any = {
            shipRate: 0,
            shipRateLoading: false,
          };
          if (!rate && showRates) {
            this.snackBar.open('No available rates.', 'Error', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          } else if (rate && rate.serviceCode) {
            updateValue.shipRate = rate.shipmentCost + rate.otherCost;
            updateValue.serviceCode = rate.serviceCode;
          }

          this.updateLineItemRate(lineItem.salesOrderId, updateValue);
        },
        error: (err) => {
          this.snackBar.open(err.error, 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.updateLineItemRate(lineItem.salesOrderId, {
            shipRate: 0,
            shipRateLoading: false,
          });
        },
      });
  }

  private updateLineItemRate(
    salesOrderId :number,
    rateConfig :{
      shipRate :number;
      shipRateLoading :boolean;
      serviceCode ?:string;
    },
  ) :void {
    this.lineItemsFormArray.controls.forEach((control :AbstractControl) => {
      const formGroup = control as UntypedFormGroup;

      if (formGroup.get('salesOrderId')?.value === salesOrderId) {
        formGroup.patchValue(rateConfig, { emitEvent: false });

        return;
      }
    });
  }

  private getLineItemWeight(weight :{ pounds :IWeight; ounces :IWeight }) :IWeight {
    const pounds = weight?.pounds;
    const ounces = weight?.ounces;

    if (ounces?.value === null || ounces?.value === 0) {
      return pounds;
    }

    if (pounds?.value > 0) {
      return pounds;
    }

    return ounces;
  }

  get confirmationOptions() :string[] {
    let options = ['none', 'delivery', 'signature', 'adult_signature'];

    if (this.form.get('carrierCode')?.value === 'fedex') {
      options.push('direct_signature');
    } else {
      if (options.includes('direct_signature')) {
        options = options.filter((option) => option !== 'direct_signature');
      }
    }

    return options;
  }

  get shipDateControl() :UntypedFormControl {
    return this.form.get('shipDate') as UntypedFormControl;
  }

  get lineItemsFormArray() :UntypedFormArray {
    return this.form.get('lineItems') as UntypedFormArray;
  }

  get invalidWarehouseAddress() :boolean {
    if (!this.data.warehouseAddress) {
      return false;
    }

    return (
      !this.data.warehouseAddress?.state ||
      !this.data.warehouseAddress?.country ||
      !this.data.warehouseAddress?.postalCode ||
      !/^-?\d+$/.test(this.data.warehouseAddress?.postalCode) ||
      !this.data.warehouseAddress?.city
    );
  }

  get selectedAllItems() :boolean {
    if (this.data?.orders?.length <= 0) {
      return false;
    }

    return this.lineItemsFormArray.controls.every(
      (lineItem :AbstractControl) => lineItem.value.selected,
    );
  }

  get selectedSomeItems() :boolean {
    return (
      this.lineItemsFormArray.controls.some(
        (lineItem :AbstractControl) => lineItem.value.selected,
      ) && !this.selectedAllItems
    );
  }

  get hasSelections() :boolean {
    return (
      (
        this.lineItemsFormArray.controls.filter(
          (lineItem :AbstractControl) => lineItem.value.selected,
        ) || []
      ).length > 0
    );
  }

  get hasMultipleShipmentsForSomeItems() :boolean {
    return (
      this.ftPartialFulfillment &&
      this.lineItemsFormArray.controls.some(
        (lineItem :AbstractControl) => lineItem.value.hasMultipleShipments,
      )
    );
  }

  get ftPartialFulfillment() :boolean {
    return this.featureFlagUtilService.isFeatureEnabled(
      'red_DV-2840_partial_fulfillment',
    );
  }
}
